"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/use-translation"

interface AtsCreateModalProps {
  isOpen: boolean
  atsPlacementId: string
  onAtsPlacementIdChange: (value: string) => void
  error: string | null
  duplicate: { atsPlacementId: number; workerName: string; clientOrgName: string } | null
  onClose: () => void
  onConfirm: () => Promise<void>
  onForce: () => Promise<void>
}

export function AtsCreateModal({
  isOpen,
  atsPlacementId,
  onAtsPlacementIdChange,
  error,
  duplicate,
  onClose,
  onConfirm,
  onForce,
}: AtsCreateModalProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const parsedId = atsPlacementId.trim() === "" ? null : Number(atsPlacementId)
  const isValidId = parsedId !== null && Number.isInteger(parsedId) && parsedId > 0

  const handleConfirm = async () => {
    if (!isValidId) return
    setIsSubmitting(true)
    try {
      await onConfirm()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleForce = async () => {
    setIsSubmitting(true)
    try {
      await onForce()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="max-w-md">
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="text-blue-900 font-semibold text-base">
            {t('atsCreateModalTitle')}
          </DialogTitle>
        </DialogHeader>
        {duplicate ? (
          <>
            <div className="space-y-3 py-4">
              <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
                {t('atsCreateDuplicate', { id: duplicate.atsPlacementId })}
              </div>
              <dl className="text-sm space-y-1">
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-600 w-16 shrink-0">{t('atsCreateDuplicateWorker')}</dt>
                  <dd className="text-gray-900">{duplicate.workerName}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium text-gray-600 w-16 shrink-0">{t('atsCreateDuplicateClient')}</dt>
                  <dd className="text-gray-900">{duplicate.clientOrgName}</dd>
                </div>
              </dl>
              <p className="text-sm text-gray-700">{t('atsCreateDuplicateQuestion')}</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="elliptical-outline" onClick={handleForce} disabled={isSubmitting}>
                {t('atsCreateProceed')}
              </Button>
              <Button variant="elliptical" onClick={handleClose} disabled={isSubmitting} autoFocus>
                {t('cancel')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="ats-placement-id" className="block text-sm font-medium mb-2 text-gray-700">
                  {t('atsPlacementIdLabel')} <span className="text-red-500">*</span>
                </label>
                <Input
                  id="ats-placement-id"
                  type="number"
                  value={atsPlacementId}
                  onChange={(e) => onAtsPlacementIdChange(e.target.value)}
                  placeholder={t('atsPlacementIdPlaceholder')}
                  disabled={isSubmitting}
                  min={1}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="elliptical-outline" onClick={handleClose} disabled={isSubmitting}>
                {t('cancel')}
              </Button>
              <Button
                variant="elliptical"
                onClick={handleConfirm}
                disabled={isSubmitting || !isValidId}
              >
                {isSubmitting ? t('atsCreatingButton') : t('atsCreateButton')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
